import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Observable, Subscription } from "rxjs";
import { PremiseFlat } from "../../../_model/store-response/premise-flat";
import { StoreFlag } from "../../../_model/store-response/store-flag";
import { SnackbarService } from "../../../service/snackbar.service";
import { StoreService } from "../../../service/store.service";
import { StoreStateService } from "../store-state.service";
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatInputModule} from '@angular/material/input';
import { StoreFlagComponent } from '../store-flag/store-flag.component';
@Component({
  selector: 'app-store-config',
  templateUrl: './store-config.component.html',
  standalone: true,
  imports: [
    CommonModule,
    StoreFlagComponent,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatCardModule,
    FormsModule,
    MatButtonModule,
    MatInputModule
  ],
  styleUrls: ['./store-config.component.scss']
})
export class StoreConfigComponent implements OnInit, OnDestroy {

  @Input() premiseFlat!: PremiseFlat;

  isCreate: boolean = false;
  isFormInvalid: boolean = true;
  isLoadingResult: boolean = false;

  incomingFormGroup!: FormGroup;
  outgoingFormGroup!: FormGroup;
  heldCalloutsFormGroup!: FormGroup;

  private incomingSubscription: Subscription | null = null;
  private outgoingSubscription: Subscription | null = null;
  private calloutsSubscription: Subscription | null = null;

  constructor(private _formBuilder: FormBuilder,
              private storeService: StoreService,
              private stateService: StoreStateService,
              private sbService: SnackbarService) {
  }

  ngOnInit() {
    this.initializeForm();
    this.setFormInitialValues();
    this.subscribeToChanges();
  }

  private initializeForm() {
    this.incomingFormGroup = this._formBuilder.group({
      incomingControlArray: this._formBuilder.array([])
    });
    this.outgoingFormGroup = this._formBuilder.group({
      outgoingControlArray: this._formBuilder.array([])
    });
    this.heldCalloutsFormGroup = this._formBuilder.group({
      heldCalloutsControlArray: this._formBuilder.array([])
    });
  }

  private setFormInitialValues() {
    if (this.premiseFlat) {
      this.populateControls(this.incomingControlArray, this.premiseFlat.incoming);
      this.populateControls(this.outgoingControlArray, this.premiseFlat.outgoing);
      this.populateControls(this.heldCalloutsControlArray, this.premiseFlat.callouts);
    }
  }

  private subscribeToChanges() {
    this.incomingSubscription = this.incomingControlArray.valueChanges.subscribe(() => this.checkFormValid());
    this.outgoingSubscription = this.outgoingControlArray.valueChanges.subscribe(() => this.checkFormValid());
    this.calloutsSubscription = this.heldCalloutsControlArray.valueChanges.subscribe(() => this.checkFormValid());
  }

  checkFormValid() {
    const incoming = this.compareArrays(this.incomingControlArray.value, this.premiseFlat.incoming);
    const outgoing = this.compareArrays(this.outgoingControlArray.value, this.premiseFlat.outgoing);
    const held = this.compareArrays(this.heldCalloutsControlArray.value, this.premiseFlat.callouts);
    this.isFormInvalid = incoming && outgoing && held;
  }

  // private compareArrays(arr1, arr2): boolean {
  //   return arr1.every((value, index) => value === arr2[index].value);
  // }

  private compareArrays<T>(arr1: T[], arr2: { value: T }[]): boolean {
  if (!arr1 || !arr2 || arr1.length !== arr2.length) {
    return false;
  }
  return arr1.every((value, index) => value === arr2[index].value);
}

  create() {
    this.isCreate = true;
  }

  save() {
    let toSave: PremiseFlat = {
      storeConfigId: this.premiseFlat.storeConfigId,
      createdDate: this.premiseFlat.createdDate,
      deletedDate: this.premiseFlat.deletedDate,
      modifiedDate: this.premiseFlat.modifiedDate,
      serviceCoverDetails: this.premiseFlat.serviceCoverDetails,
      premiseDetails: this.premiseFlat.premiseDetails,
      incoming: this.createFlagsFromCtrl(this.premiseFlat.incoming, this.incomingControlArray),
      outgoing: this.createFlagsFromCtrl(this.premiseFlat.outgoing, this.outgoingControlArray),
      callouts: this.createFlagsFromCtrl(this.premiseFlat.callouts, this.heldCalloutsControlArray)
    };
    if (toSave.storeConfigId) {
      this.executeRequest(this.storeService.updateStoreConfig(toSave));
    } else {
      this.executeRequest(this.storeService.createStoreConfig(toSave));
    }
  }

  private createFlagsFromCtrl(flags: StoreFlag[], ctrl: FormArray): StoreFlag[] {
    let toReturn: StoreFlag[] = [];

    let i = 0;
    for (const flag of flags) {
      toReturn[i] = {
        property: flag.property,
        displayName: flag.displayName,
        value: ctrl.value[i]
      };
      i++;
    }
    return toReturn;
  }

  private executeRequest(observable$: Observable<any>) {
    this.isLoadingResult = true;
    observable$.subscribe({
      next: () => {
        this.isLoadingResult = false;
        this.sbService.openDefault('Store Config is saved');
        this.stateService.isStoreChanged(true);
      },
      error: () => {
        this.isLoadingResult = false;
        this.stateService.isStoreChanged(true);
      }
    });
  }

  discard() {
    if (this.isCreate) {
      this.isCreate = false;
    } else {
      this.initializeForm();
      this.setFormInitialValues();
      this.checkFormValid();
      this.subscribeToChanges();
    }
  }

  private get incomingControlArray() {
    return this.incomingFormGroup.controls['incomingControlArray'] as FormArray;
  }


  private get outgoingControlArray() {
    return this.outgoingFormGroup.controls['outgoingControlArray'] as FormArray;
  }

  private get heldCalloutsControlArray() {
    return this.heldCalloutsFormGroup.controls['heldCalloutsControlArray'] as FormArray;
  }

  private populateControls(controlArray: FormArray, properties: StoreFlag[]) {
    for (const property of properties) {
      controlArray.push(this._formBuilder.control(property.value))
    }
  }

  ngOnDestroy(): void {
    if (this.incomingSubscription) {
    this.incomingSubscription.unsubscribe();
  } else if(this.outgoingSubscription) {
    this.outgoingSubscription.unsubscribe();
  } else if(this.calloutsSubscription) {
    this.calloutsSubscription.unsubscribe();
  }
 }
}
